import { Question } from '../types';

// Type for questions with at least the fields we need for weight calculations
// Updated to use label instead of questionId
type QuestionForWeight = {
  questionId: string; // This is actually label || questionId || id (for backward compatibility)
  weighting?: number | string | null;
};

/**
 * Calculate weight percentage for a question within its dimension
 * Formula: P_i = (w_i / sum(w_j)) * 100
 * 
 * @param questionRawWeight - The raw weight of the question
 * @param dimensionQuestions - All questions in the same dimension
 * @returns The weight percentage (0-100)
 */
export function calculateWeightPercentage(
  questionRawWeight: number,
  dimensionQuestions: QuestionForWeight[]
): number {
  const totalRawWeight = dimensionQuestions.reduce((sum, q) => {
    const weight = typeof q.weighting === 'number' 
      ? q.weighting 
      : (q.weighting ? parseFloat(q.weighting.toString()) : 1.0);
    return sum + weight;
  }, 0);

  if (totalRawWeight === 0) return 0;
  
  return (questionRawWeight / totalRawWeight) * 100;
}

/**
 * Calculate weight percentages for all questions in a dimension
 * 
 * @param dimensionQuestions - All questions in the dimension
 * @returns Map of questionId to weight percentage
 */
export function calculateDimensionWeightPercentages(
  dimensionQuestions: QuestionForWeight[]
): Map<string, number> {
  const percentages = new Map<string, number>();
  
  dimensionQuestions.forEach(q => {
    const rawWeight = typeof q.weighting === 'number' 
      ? q.weighting 
      : (q.weighting ? parseFloat(q.weighting.toString()) : 1.0);
    
    const percentage = calculateWeightPercentage(rawWeight, dimensionQuestions);
    percentages.set(q.questionId, percentage);
  });
  
  return percentages;
}

/**
 * Reverse-calculate raw weight from a desired percentage
 * Formula: w_new = (P_new / 100) * (sum_existing - w_old) / (1 - P_new / 100)
 * 
 * Simplified: w_new = (P_new * (sum_existing - w_old)) / (100 - P_new)
 * 
 * @param desiredPercentage - The desired weight percentage (0-100)
 * @param currentRawWeight - The current raw weight of the question being edited
 * @param dimensionQuestions - All questions in the same dimension (including the one being edited)
 * @returns The new raw weight value
 */
export function calculateRawWeightFromPercentage(
  desiredPercentage: number,
  currentRawWeight: number,
  dimensionQuestions: QuestionForWeight[]
): number {
  // Calculate sum of all raw weights in the dimension
  const totalRawWeight = dimensionQuestions.reduce((sum, q) => {
    const weight = typeof q.weighting === 'number' 
      ? q.weighting 
      : (q.weighting ? parseFloat(q.weighting.toString()) : 1.0);
    return sum + weight;
  }, 0);

  // If desired percentage is 100%, we need special handling
  if (desiredPercentage >= 100) {
    // Set this question to be the only one with weight, others become 0
    // But that's probably not desired, so cap at 99%
    desiredPercentage = 99;
  }

  // If desired percentage is 0%, set raw weight to a very small value (not 0 to avoid division issues)
  if (desiredPercentage <= 0) {
    return 0.01;
  }

  // Calculate sum excluding the current question's weight
  const sumExcludingCurrent = totalRawWeight - currentRawWeight;

  // Formula: w_new = (P_new * (sum_existing - w_old)) / (100 - P_new)
  const newRawWeight = (desiredPercentage * sumExcludingCurrent) / (100 - desiredPercentage);

  // Ensure minimum weight of 0.01
  return Math.max(0.01, newRawWeight);
}

/**
 * Calculate the average raw weight for questions in a dimension
 * Used when adding a new question - it should get the average weight
 * 
 * @param dimensionQuestions - All questions in the dimension (excluding the new one)
 * @returns The average raw weight
 */
export function calculateAverageRawWeight(
  dimensionQuestions: QuestionForWeight[]
): number {
  if (dimensionQuestions.length === 0) {
    return 1.0; // Default weight if no questions exist
  }

  const totalRawWeight = dimensionQuestions.reduce((sum, q) => {
    const weight = typeof q.weighting === 'number' 
      ? q.weighting 
      : (q.weighting ? parseFloat(q.weighting.toString()) : 1.0);
    return sum + weight;
  }, 0);

  return totalRawWeight / dimensionQuestions.length;
}

/**
 * Get the raw weight value from a question
 * Handles different types (number, Decimal, string)
 * 
 * @param question - The question object (can be Question or QuestionForWeight)
 * @returns The raw weight as a number
 */
export function getQuestionRawWeight(question: QuestionForWeight | { weighting?: number | string | null }): number {
  if (typeof question.weighting === 'number') {
    return question.weighting;
  }
  if (question.weighting) {
    return parseFloat(question.weighting.toString());
  }
  return 1.0; // Default weight
}

